/**
 * §P.8 · Cuándo entramos en modo soporte, quién y con qué motivo.
 *
 * ## El motivo es lo único que importa de esta pantalla
 *
 * `BFF_BO.md` §8.1 obliga a escribirlo al abrir la sesión y avisa de que **es texto
 * libre que alguien va a leer**. Ese alguien es el club, aquí. Una lista de fechas
 * sin motivos diría «entramos catorce veces» y nada más, que es peor que no decir
 * nada: da la cifra del susto sin la explicación.
 *
 * Por eso el motivo va en grande y la fecha en pequeño, al revés de como se pinta
 * una lista de eventos.
 *
 * ## Una sesión abierta se enseña distinta
 *
 * No es histórico: es que **ahora mismo** hay alguien de Kiap dentro. Eso se mira de
 * otra manera, y mezclarla entre las cerradas con un guion donde va la duración la
 * esconde justo cuando es lo único que se quiere ver.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fechaYHora } from '@/shared/ui/formato';
import {
  duracionDeSoporte,
  type SesionDeSoporte,
} from '@/club/cumplimiento/tipos';

export function ModoSoporte({ club }: { club: string }) {
  const sesiones = useQuery({
    queryKey: ['soporte', club],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['support-sessions'].get({ query: { limit: 200 } });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as SesionDeSoporte[];
    },
  });

  const lista = sesiones.data ?? [];
  const abiertas = lista.filter((s) => s.ended_at === null);

  return (
    <section>
      <h1>Modo soporte</h1>

      <p className="tenue">
        Cuando pides ayuda, alguien de Kiap puede entrar en tu club para verlo
        con sus ojos. Tiene que escribir un motivo antes de entrar, y{' '}
        <strong>ese motivo aparece aquí</strong>, junto a cuándo fue y cuánto
        duró.
      </p>

      <p className="tenue">
        Entra <strong>solo para mirar</strong>. Si para ayudarte tiene que
        cambiar algo, lo dice aquí con la hora, y{' '}
        <strong>cada cambio queda en tu registro de accesos</strong>. Tus chats
        con las familias no los abre nunca.
      </p>

      {sesiones.isPending && (
        <Cargando filas={3} etiqueta="Cargando las sesiones de soporte" />
      )}

      {sesiones.isError && (
        <PanelError
          problema={aProblema(sesiones.error)}
          reintentar={() => void sesiones.refetch()}
        />
      )}

      {/*
        Lo de ahora antes que lo de antes. Que haya alguien dentro en este momento no
        es un elemento más de una lista cronológica.
      */}
      {abiertas.length > 0 && (
        <div className="aviso" role="status">
          <strong>
            {abiertas.length === 1
              ? 'Ahora mismo hay una sesión de soporte abierta.'
              : `Ahora mismo hay ${abiertas.length} sesiones de soporte abiertas.`}
          </strong>{' '}
          {abiertas.map((s) => s.staff_name ?? 'Alguien de Kiap').join(', ')}{' '}
          está mirando tu club. Se cierra sola, y cuando lo haga aparecerá aquí
          cuánto duró.
        </div>
      )}

      {sesiones.data && lista.length === 0 && (
        <Vacio
          titulo="Nunca hemos entrado"
          explicacion="Nadie de Kiap ha entrado en modo soporte en este club. Si algún día hace falta, tendrá que escribir un motivo y aparecerá en esta lista."
        />
      )}

      {lista.length > 0 && (
        <ul className="soportes">
          {lista.map((s) => (
            <li key={s.id} className={s.ended_at ? undefined : 'fila-marcada'}>
              {/* El motivo en grande: es lo que se viene a leer. */}
              <p className="soporte-motivo">{s.reason}</p>
              <p className="tenue">
                {s.staff_name ?? 'Personal de Kiap'} ·{' '}
                {fechaYHora(s.started_at)} · {duracionDeSoporte(s)}
              </p>
              <p className="tenue">
                {s.write_enabled_at ? (
                  <span className="pastilla pastilla-espera">
                    Pudo cambiar datos desde el {fechaYHora(s.write_enabled_at)}
                  </span>
                ) : (
                  <span className="pastilla pastilla-info">Solo miró</span>
                )}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
